<template>
  <div class="table" :class="[tableStatusClass, { selected: selected }]">
    <a class="table-container" @click="onCardClick" @contextmenu.prevent="onRightClick">
      <div v-if="table.status_id !== 1" class="table-length">
        <span class="badge ts-font-semibold" :class="false ? 'bg-overtime' : ''">
          <span>{{ minutesUsed }}</span>
        </span>
      </div>
      <div v-if="hasMultipleInvoices" class="split-badge" @click.stop="showSplitInvoicesModal = true">
        <i class="fa fa-cut"></i> Split &mdash; {{ openInvoices.length }} bills
      </div>
      <div class="table-status"></div>
      <div class="table-name">
        <div class="name">{{ $t('restaurantSale.table') }}</div>
        <div class="no">{{ table.table_no }}</div>
        <div class="countdown">
          <span v-if="table.status_id === 3" class="countdown-timer">
            <span>{{ minutesLeft }}</span>
          </span>
          <span v-else-if="hasBooking && table.status_id == 1" class="status-text reserved-clickable" @click.stop="showBookingModal = true">
            <i class="fa fa-bookmark"></i> Reserved
          </span>
          <span v-else class="status-text">
            {{ getStatusText() }}
          </span>
        </div>
      </div>
      <div class="table-info-row">
        <div class="table-info-col">
          <div class="table-info-container">
            <span class="icon">
              <span v-show="table.status_id != 1">🕐</span>
            </span>
            <span class="text">{{ table.start_time }}</span>
          </div>
        </div>
        <div class="table-info-col">
          <div class="table-info-container">
            <span class="icon">
              <span v-show="table.status_id != 1 && hasTimeLimit">⏳</span>
            </span>
            <span class="text">{{ endTime }}</span>
          </div>
        </div>
      </div>
      <div class="table-info-row">
        <div class="table-info-col">
          <div class="table-info-container">
            <span class="icon">
              <span v-show="table.status_id != 1">💰</span>
            </span>
            <span class="text">{{ defaultCurrency }}
              {{ table.invoice_amount | format_number }}</span>
          </div>
        </div>
        <div class="table-info-col">
          <div class="table-info-container">
            <span class="icon">
              <span v-show="table.status_id != 1">💵</span>
            </span>
            <span class="text">{{ table.invoice_status }}</span>
          </div>
        </div>
      </div>
    </a>
    <a-modal v-model="showBookingModal" :footer="null" :z-index="10000" @cancel="showBookingModal = false">
      <template slot="title">
        <span class="booking-modal-title">
          <i class="fa fa-bookmark"></i>
          Reservations &mdash; Field {{ table.table_no }}
        </span>
      </template>

      <div v-if="tableBookings.length === 0" class="booking-empty">
        <i class="fa fa-calendar-times booking-empty-icon"></i>
        <p>No reservations found</p>
      </div>

      <div v-for="(booking, i) in tableBookings" :key="booking.booking_id || i"
        class="booking-card" :class="{ 'booking-card-mt': i > 0 }">

        <div class="booking-card-header">
          <div class="booking-avatar">
            {{ booking.customer && booking.customer.customer_name ? booking.customer.customer_name.charAt(0).toUpperCase() : '?' }}
          </div>
          <div class="booking-customer-info">
            <div class="booking-customer-name">
              {{ booking.customer ? booking.customer.customer_name : 'Unknown Customer' }}
            </div>
            <div v-if="booking.customer && booking.customer.contact_number" class="booking-customer-phone">
              <i class="fa fa-phone"></i> {{ booking.customer.contact_number }}
            </div>
          </div>
          <div v-if="booking.available_schedule" class="booking-schedule-badge">
            <i class="fa fa-clock"></i> {{ booking.available_schedule.schedule_time }}
          </div>
        </div>

        <div class="booking-meta-grid">
          <div class="booking-meta-item">
            <i class="fa fa-calendar booking-meta-icon"></i>
            <div>
              <div class="booking-meta-label">Date</div>
              <div class="booking-meta-val">{{ booking.booking_date }}</div>
            </div>
          </div>
          <div v-if="booking.num_person" class="booking-meta-item">
            <i class="fa fa-users booking-meta-icon"></i>
            <div>
              <div class="booking-meta-label">Players</div>
              <div class="booking-meta-val">{{ booking.num_person }} person{{ booking.num_person > 1 ? 's' : '' }}</div>
            </div>
          </div>
          <div v-if="booking.purpose" class="booking-meta-item">
            <i class="fa fa-flag booking-meta-icon"></i>
            <div>
              <div class="booking-meta-label">Purpose</div>
              <div class="booking-meta-val">{{ booking.purpose }}</div>
            </div>
          </div>
          <div v-if="booking.customer && booking.customer.company_name" class="booking-meta-item">
            <i class="fa fa-building booking-meta-icon"></i>
            <div>
              <div class="booking-meta-label">Company</div>
              <div class="booking-meta-val">{{ booking.customer.company_name }}</div>
            </div>
          </div>
        </div>

        <a-button
          type="primary"
          block
          :loading="checkingInIds.includes(booking.booking_id)"
          class="booking-checkin-btn"
          @click="onCheckIn(booking)"
        >
          <i class="fa fa-sign-in-alt"></i> Check In
        </a-button>
      </div>
    </a-modal>

    <a-modal v-model="showSplitInvoicesModal" :footer="null" :z-index="10000" :width="560" @cancel="showSplitInvoicesModal = false">
      <template slot="title">
        <span class="split-invoices-modal-title">
          <i class="fa fa-cut"></i>
          Choose a Bill to Settle
          <span class="split-invoices-modal-sub">&mdash; Field {{ table.table_no }}</span>
        </span>
      </template>

      <div class="split-invoices-summary">
        <div class="summary-block">
          <div class="summary-label">Open Bills</div>
          <div class="summary-value">{{ openInvoices.length }}</div>
        </div>
        <div class="summary-divider"></div>
        <div class="summary-block">
          <div class="summary-label">Total Owed</div>
          <div class="summary-value summary-total">{{ defaultCurrency }} {{ formatMoney(totalOwed) }}</div>
        </div>
      </div>

      <div v-for="(inv, i) in openInvoices" :key="inv.stock_out_id"
        class="split-invoice-card" :class="{ 'split-invoice-mt': i > 0 }"
        @click="onPickInvoice(inv)">
        <div class="split-invoice-left">
          <div class="split-invoice-tag-row">
            <div class="split-invoice-tag">Bill {{ String.fromCharCode(65 + i) }}</div>
            <div class="split-invoice-no">#{{ inv.order_no }}</div>
          </div>
          <div class="split-invoice-meta">
            <span><i class="fa fa-list-ul"></i> {{ (inv.invoice_detail || []).length }} item{{ (inv.invoice_detail || []).length > 1 ? 's' : '' }}</span>
            <span v-if="Number(inv.discount) > 0"><i class="fa fa-tag"></i> Disc {{ defaultCurrency }} {{ formatMoney(inv.discount) }}</span>
            <span v-if="formatBillDate(inv.stock_out_date)"><i class="fa fa-clock"></i> {{ formatBillDate(inv.stock_out_date) }}</span>
          </div>
          <div v-if="(inv.invoice_detail || []).length" class="split-invoice-items">
            <span v-for="(d, di) in (inv.invoice_detail || []).slice(0, 3)" :key="di" class="split-invoice-item">
              {{ d.product_name_en || d.product_name_kh || ('#' + d.product_id) }}
              <span class="qty">×{{ formatQty(d.quantity) }}</span>
            </span>
            <span v-if="(inv.invoice_detail || []).length > 3" class="split-invoice-item more">
              +{{ (inv.invoice_detail || []).length - 3 }} more
            </span>
          </div>
        </div>
        <div class="split-invoice-right">
          <div class="split-invoice-amount">
            {{ defaultCurrency }} {{ formatMoney(inv.grand_total) }}
          </div>
          <div class="split-invoice-action">
            <i class="fa fa-arrow-right"></i> Settle
          </div>
        </div>
      </div>

      <div v-if="openInvoices.length === 0" class="split-invoices-empty">
        <i class="fa fa-check-circle"></i>
        <p>All bills on this table are settled.</p>
      </div>
    </a-modal>

    <div v-if="showMenu" class="context-menu" :style="{ top: menuY + 'px', left: menuX + 'px' }">
      <List size="small">
        <ListItem>
          <a @click="onNomalCheckout">
            <span class="ts-space-x-1">
              <i class="fa fa-calendar-check"></i>
              <span>{{ $t('restaurantSale.nomarlCheckIn') }}</span>
            </span>
          </a>
        </ListItem>
        <ListItem v-if="table.guest_level_id == 1">
          <a @click="onMemeberCheckout">
            <span class="ts-space-x-1">
              <i class="fa fa-id-card"></i>
              <span>{{ $t('restaurantSale.memberCheckIn') }}</span>
            </span>
          </a>
        </ListItem>
      </List>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

let _audioCtx = null
function getAudioCtx() {
  if (!_audioCtx) {
    try { _audioCtx = new (window.AudioContext || window.webkitAudioContext)() } catch (e) {}
  }
  return _audioCtx
}
if (typeof window !== 'undefined') {
  const resumeCtx = () => { const c = getAudioCtx(); if (c && c.state === 'suspended') c.resume() }
  ;['click', 'keydown', 'touchstart'].forEach(e => document.addEventListener(e, resumeCtx, { capture: true, passive: true }))
}

export default {
  name: 'TableCard',
  props: {
    table: {
      type: Object,
      required: true
    },
    selected: {
      type: Boolean,
      default: false
    },
    defaultCurrency: {
      type: String,
      default: ''
    }
  },
  filters: {
    format_number(value) {
      if (value) {
        return `${parseFloat(value).toLocaleString()}`
      }
      return 0
    }
  },
  computed: {
    hasTimeLimit() {
      return this.getMinEndTime() !== null;
    },

    hasBooking() {
      return !!(this.table.bookings && this.table.bookings.length > 0)
    },
    openInvoices() {
      return this.table.open_invoices || []
    },
    hasMultipleInvoices() {
      if (this.openInvoices.length <= 1) return false
      return this.openInvoices.some(inv => inv.is_splited)
    },
    totalOwed() {
      return this.openInvoices.reduce((sum, inv) => sum + Number(inv.grand_total || 0), 0)
    },
    tableBookings() {
      return (this.table.bookings || []).slice().sort((a, b) => {
        const toMinutes = t => {
          if (!t) return 0
          const m = t.match(/(\d+):(\d+)\s*(am|pm)/i)
          if (!m) return 0
          let h = parseInt(m[1]), min = parseInt(m[2]), period = m[3].toLowerCase()
          if (period === 'pm' && h !== 12) h += 12
          if (period === 'am' && h === 12) h = 0
          return h * 60 + min
        }
        const at = a.available_schedule ? a.available_schedule.schedule_time : ''
        const bt = b.available_schedule ? b.available_schedule.schedule_time : ''
        return toMinutes(at) - toMinutes(bt)
      })
    },

    tableStatusClass() {
      // Check for over time alert first (assuming status_id 4 or a new field)
      if (this.table.status_id == 4 || this.table.over_time_alert) {
        return 'over-time-alert'
      }

      // Existing status checks
      if (this.table.status_id == 1) {
        return this.hasBooking ? 'reserved' : 'available'
      }
      if (this.table.status_id == 2) {
        return 'disabled'
      }
      if (this.table.status_id == 3) {
        // Additional check for over time within in-use status
        if (this.hasTimeLimit && this.isTableOverTime()) {
          return 'over-time-alert'
        }
        if (this.hasTimeLimit && this.isTableNearTime()) {
          return 'near-time-alert'
        }
        return 'in-use'
      }

      return 'available' // fallback
    },

    formattedCountdown() {
      if (this.table.status_id !== 3 || !this.hasTimeLimit) {
        return ''
      }

      const timeLeft = this.calculateTimeLeft()

      if (timeLeft <= 0) {
        return 'OVERTIME'
      }

      const minutes = Math.floor(timeLeft / (60 * 1000))
      const seconds = Math.floor((timeLeft % (60 * 1000)) / 1000)

      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    },

    minutesUsed() {
      if (!this.table.start_time) {
        return 'N/A'
      }
      const startTime = this.parseTimeString(this.table.start_time)
      const currentTime = new Date()
      const elapsedTime = currentTime - startTime

      const totalMinutes = Math.ceil(elapsedTime / (60 * 1000)) // Round up minutes
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60

      if (hours > 0) {
        return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
      }

      return `${minutes}m`
    },

    minutesLeft() {
      if (this.table.status_id !== 3) {
        return ''
      }

      if (this.table.main_linked_key) {
        return 'Linked to: ' + this.table.main_linked_key.table_no
      }

      if (!this.table.start_time) {
        return 'Waiting'
      }

      // Unlimited time case
      if (!this.hasTimeLimit) {
        return 'Unlimited'
      }

      const timeLeft = this.calculateTimeLeft()

      if (timeLeft <= 0) {
        return 'OVERTIME'
      }

      const totalMinutes = Math.ceil(timeLeft / (60 * 1000)) // Round up minutes
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60

      if (hours > 0) {
        return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
      }

      return `${minutes}m`
    },

    endTime() {
      if (!this.table.start_time) {
        return ''
      }

      if (!this.hasTimeLimit) {
        return 'Unlimited'
      }
      if (!this.table.start_time) {
        return 'Unlimited'
      }

      // Get the max service length from invoice_detail
      const timeLimitInMs = this.getMinEndTime()

      return timeLimitInMs.format("hh:mm A");
    }
  },
  methods: {
    onNomalCheckout() {
      this.showMenu = false;
      this.$emit('click');
    },
    onMemeberCheckout() {
      this.$emit('member-checkout')
      this.showMenu = false;
    },
    onRightClick(e) {
      this.showMenu = false;
      if (this.$el.contains(e.target)) {
        if (this.table.status_id != 1) {
          return;
        }
        const menuWidth = 135; // approximate menu width
        const menuHeight = 74; // approximate menu height

        const { innerWidth, innerHeight } = window;

        let x = e.clientX;
        let y = e.clientY;

        if (x + menuWidth > innerWidth) x = innerWidth - menuWidth - 8;
        if (y + menuHeight > innerHeight) y = innerHeight - menuHeight - 8;

        this.menuX = x;
        this.menuY = y;
        this.showMenu = true;
      }
    },
    closeMenu(e) {
      if (!this.$el.contains(e.target)) {
        this.showMenu = false;
      }
    },
    getMinEndTime() {
      //Main linked table case
      if (this.table.main_linked_key) {
        const table = this.$store.state.restaurantSale.counterCheckout.tables.find(el => el.table_id == this.table.main_linked_key.table_id);

        if (!table.invoice_detail || !Array.isArray(table.invoice_detail)) {
          return null // No time limit
        }

        const minEndTime = table.invoice_detail
          .filter(item => item.start_time != null && item.end_time == null && item.service_length)
          .map(item => moment(item.start_time).add(item.service_length * (parseInt(item.quantity) || 1), 'minutes'))
          .reduce((max, current) =>
            max == null || current.isAfter(max) ? current : max, null)

        if (!minEndTime) {
          return null // No time limit
        }

        return minEndTime
      }
      // Find the latest end time from invoice_detail items
      if (!this.table.invoice_detail || !Array.isArray(this.table.invoice_detail)) {
        return null // No time limit
      }

      const minEndTime = this.table.invoice_detail
        .filter(item => item.start_time != null && item.end_time == null && item.service_length)
        .map(item => moment(item.start_time).add(item.service_length * (parseInt(item.quantity) || 1), 'minutes'))
        .reduce((max, current) =>
          max == null || current.isAfter(max) ? current : max, null)

      if (!minEndTime) {
        return null // No time limit
      }

      return minEndTime
    },

    isTableOverTime() {
      // Check if table has been occupied for more than the max service length
      if (!this.table.start_time || !this.hasTimeLimit) {
        return false
      }

      const now = moment()

      if (!this.table.start_time) {
        return false
      }

      const minEndTime = this.getMinEndTime()

      if (minEndTime) {
        return now.isAfter(minEndTime);
      }

      return false
    },
    isTableNearTime() {
      const timeLeft = this.calculateTimeLeft()
      return timeLeft > 0 && timeLeft <= 15 * 60 * 1000
    },
    calculateTimeLeft() {
      if (!this.table.start_time || !this.hasTimeLimit) {
        return 0
      }

      const now = moment()

      if (!this.table.start_time) {
        return 0
      }

      const minEndTime = this.getMinEndTime()

      if (minEndTime) {
        if (now.isBefore(minEndTime)) {
          return minEndTime.diff(now);
        }
      }
      return 0;
    },

    parseTimeString(timeStr) {
      try {
        // Parse time string like "11:17 AM"
        const today = new Date()
        const [time, period] = timeStr.trim().split(' ')
        const [hours, minutes] = time.split(':')

        let hour24 = parseInt(hours)
        const min = parseInt(minutes)

        // Convert to 24-hour format
        if (period.toUpperCase() === 'PM' && hour24 !== 12) {
          hour24 += 12
        } else if (period.toUpperCase() === 'AM' && hour24 === 12) {
          hour24 = 0
        }

        // Create date object with today's date but specified time
        const parsedTime = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          hour24,
          min,
          0
        )

        // Handle case where start_time might be from yesterday (past midnight scenario)
        const now = new Date()
        if (parsedTime > now) {
          // If parsed time is in the future, assume it was from yesterday
          parsedTime.setDate(parsedTime.getDate() - 1)
        }

        return parsedTime
      } catch (error) {
        console.warn('Failed to parse time string:', timeStr, error)
        return null
      }
    },

    getStatusText() {
      switch (this.table.status_id) {
        case 1:
          return this.hasBooking ? 'Reserved' : 'Available'
        case 2:
          return 'Disabled'
        case 4:
          return 'Over Time'
        default:
          return ''
      }
    },

    startCountdownInterval() {
      if (this.countdownInterval) return
      this.countdownInterval = setInterval(() => {
        const nearTime = this.isTableNearTime()
        const overTime = this.isTableOverTime()
        const now = Date.now()

        if (overTime) {
          if (this.lastOverTimeBeep === 0 || now - this.lastOverTimeBeep >= 30000) {
            this.lastOverTimeBeep = now
            this.playBeep(900, 0.15, 3, 0.1)
          }
        } else if (nearTime) {
          if (this.lastNearTimeBeep === 0 || now - this.lastNearTimeBeep >= 30000) {
            this.lastNearTimeBeep = now
            this.playBeep(600, 0.2, 2, 0.2)
          }
        } else {
          this.lastNearTimeBeep = 0
          this.lastOverTimeBeep = 0
        }

        // Auto-stop only when there are multiple service products and a next one is waiting
        const items = this.table.invoice_detail || []
        const serviceItems = items.filter(i => i.service_length)
        if (serviceItems.length > 1 && serviceItems.some(i => !i.start_time)) {
          serviceItems.forEach(item => {
            if (item.start_time && !item.end_time) {
              const endTime = moment(item.start_time).add(item.service_length * (parseInt(item.quantity) || 1), 'minutes')
              const key = `${item.stock_out_id}_${item.product_id}_${item.start_time}`
              if (moment().isAfter(endTime) && !this.autoStoppedServices.has(key)) {
                this.autoStoppedServices.add(key)
                this.autoStopAndStartNext(item)
              }
            }
          })
        }

        this.$forceUpdate()
      }, 1000)
    },

    autoStopAndStartNext(item) {
      this.$store.dispatch('restaurantSale/counterCheckout/serviceDurationDetail', {
        stock_out_id: item.stock_out_id,
        product_id: item.product_id,
        ref_table_id: item.ref_table_id,
        start_time: item.start_time,
        end_time: item.end_time,
        service_length: item.service_length,
        is_pre_charge: item.is_pre_charge,
        action_type: 'end-time'
      }).then(() => {
        const allItems = this.table.invoice_detail || []
        const nextItem = allItems
          .filter(i => i.service_length && !i.start_time)
          .sort((a, b) => b.display_order - a.display_order)[0]

        if (!nextItem) return

        return this.$store.dispatch('restaurantSale/counterCheckout/serviceDurationDetail', {
          stock_out_id: nextItem.stock_out_id,
          product_id: nextItem.product_id,
          ref_table_id: nextItem.ref_table_id,
          start_time: nextItem.start_time,
          end_time: nextItem.end_time,
          service_length: nextItem.service_length,
          is_pre_charge: nextItem.is_pre_charge,
          action_type: 'start-time',
          confrim: true
        })
      }).finally(() => {
        this.$store.dispatch('restaurantSale/counterCheckout/fetchTables').catch(() => {})
      })
    },

    onCardClick() {
      if (this.hasMultipleInvoices) {
        this.showSplitInvoicesModal = true
        return
      }
      this.$emit('click')
    },
    onPickInvoice(inv) {
      this.showSplitInvoicesModal = false
      this.$emit('pick-invoice', { table: this.table, invoice: inv })
    },
    formatMoney(n) {
      const v = Number(n || 0)
      return v.toFixed(2)
    },
    formatBillDate(d) {
      if (!d) return ''
      const m = moment(d, [moment.ISO_8601, 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DDTHH:mm:ss'], true)
      const m2 = m.isValid() ? m : moment(d)
      return m2.isValid() ? m2.format('DD MMM, HH:mm') : ''
    },
    formatQty(value) {
      const n = Number(value || 0)
      if (Number.isInteger(n)) return n
      return parseFloat(n.toFixed(4)).toString()
    },
    onCheckIn(booking) {
      this.checkingInIds = [...this.checkingInIds, booking.booking_id]
      this.$store.dispatch('restaurantSale/counterCheckout/isCustomerCome', booking.booking_id)
        .then(response => {
          const checkinData = response && response.data ? response.data : {}
          this.$store.dispatch('restaurantSale/counterCheckout/fetchTables')
            .then(() => {
              this.showBookingModal = false
              this.$emit('check-in', {
                table_id: this.table.table_id,
                num_person: checkinData.num_person || null,
                customer_id: checkinData.customer_id || null
              })
            })
            .catch(() => {})
        })
        .finally(() => {
          this.checkingInIds = this.checkingInIds.filter(id => id !== booking.booking_id)
        })
    },

    playBeep(frequency, duration, count, gap) {
      try {
        const ctx = getAudioCtx()
        if (!ctx || ctx.state !== 'running') return
        const play = (i) => {
          if (i >= count) return
          const osc = ctx.createOscillator()
          const gain = ctx.createGain()
          osc.connect(gain)
          gain.connect(ctx.destination)
          osc.frequency.value = frequency
          osc.type = 'sine'
          gain.gain.setValueAtTime(1.0, ctx.currentTime)
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration)
          osc.start(ctx.currentTime)
          osc.stop(ctx.currentTime + duration)
          setTimeout(() => play(i + 1), (duration + gap) * 1000)
        }
        play(0)
      } catch (e) { /* audio not supported */ }
    }
  },

  watch: {
    'table.status_id'(val) {
      if (val === 3 && this.hasTimeLimit) this.startCountdownInterval()
    },
    'table.invoice_detail'() {
      if (this.table.status_id === 3 && this.hasTimeLimit) this.startCountdownInterval()
    }
  },

  mounted() {
    document.addEventListener('click', this.closeMenu);
    document.addEventListener('contextmenu', this.onRightClick);
    if (this.table.status_id === 3 && this.hasTimeLimit) {
      this.startCountdownInterval()
    }
  },

  beforeDestroy() {
    document.removeEventListener('click', this.closeMenu);
    document.removeEventListener('contextmenu', this.onRightClick);
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
  },
  data() {
    return {
      countdownInterval: null,
      lastNearTimeBeep: 0,
      lastOverTimeBeep: 0,
      showMenu: false,
      menuX: 0,
      menuY: 0,
      autoStoppedServices: new Set(),
      showBookingModal: false,
      checkingInIds: [],
      showSplitInvoicesModal: false
    }
  }
}
</script>

<style scoped>
/* Add styles for countdown timer */
.countdown {
  font-weight: bold;
}

.countdown-timer {
  color: #007bff;
  /* Blue color for active countdown */
  font-size: 1.1em;
}

.countdown-timer.overtime {
  color: #dc3545;
  /* Red color for overtime */
  animation: blink 1s infinite;
}

.status-text {
  color: #6c757d;
  /* Gray color for status text */
  font-size: 0.9em;
}

@keyframes blink {

  0%,
  50% {
    opacity: 1;
  }

  51%,
  100% {
    opacity: 0.3;
  }
}

/* Additional styling for over-time-alert tables */
.table.over-time-alert .countdown-timer {
  color: #dc3545;
  animation: blink 1s infinite;
}

/* 15-minute pre-warning */
.table.near-time-alert .countdown-timer {
  color: #fd7e14;
  animation: blink 1s infinite;
}

.context-menu {
  padding: 6px 0px;
  position: fixed;
  /* so it follows cursor */
  background: white;
  border: 1px solid #ccc;
  z-index: 9999;
  box-shadow: 0 2px 6px rgba(0, 0, 0, .15);
  border-radius: 4px;
}

.ivu-list-item {
  white-space: nowrap;
  padding: 6px 8px;
}

.ivu-list-item:hover {
  background-color: #e2e8f0;
}

.table-length {
  position: absolute;
  top: 10px;
  left: 5px;
  height: 8px;
  display: flex;
  align-items: center;
}

.bg-overtime {
  background: rgba(255, 69, 0, 0.8);
}

.pos-counter .pos-counter-body .pos-counter-content .table-row .table.over-time-alert .table-container:after {
  display: none;
}

.reserved-clickable {
  color: #7c3aed;
  cursor: pointer;
  font-weight: 600;
}
.reserved-clickable:hover {
  text-decoration: underline;
}

/* Modal title */
.booking-modal-title {
  color: #7c3aed;
  font-weight: 700;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Empty state */
.booking-empty {
  text-align: center;
  padding: 32px 0;
  color: #aaa;
}
.booking-empty-icon {
  font-size: 36px;
  margin-bottom: 10px;
  display: block;
}

/* Booking card */
.booking-card {
  border: 1px solid #ede9fe;
  border-radius: 10px;
  padding: 14px 16px 12px;
  background: #faf8ff;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.booking-card-mt {
  margin-top: 12px;
}

/* Card header */
.booking-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
}
.booking-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7c3aed, #a78bfa);
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.booking-customer-info {
  flex: 1;
  min-width: 0;
}
.booking-customer-name {
  font-weight: 700;
  font-size: 14px;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.booking-customer-phone {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}
.booking-customer-phone .fa {
  color: #a78bfa;
}

/* Schedule badge */
.booking-schedule-badge {
  flex-shrink: 0;
  background: #ede9fe;
  color: #6d28d9;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  white-space: nowrap;
}
.booking-schedule-badge .fa {
  margin-right: 4px;
}

/* Meta grid */
.booking-meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 8px;
  background: #fff;
  border-radius: 8px;
  padding: 10px 12px;
  border: 1px solid #ede9fe;
}
.booking-meta-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.booking-meta-icon {
  color: #a78bfa;
  font-size: 13px;
  margin-top: 2px;
  width: 14px;
  text-align: center;
}
.booking-meta-label {
  font-size: 10px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.2;
}
.booking-meta-val {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  line-height: 1.3;
}

/* Check In button */
.booking-checkin-btn {
  background: #16a34a !important;
  border-color: #16a34a !important;
  color: #fff !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  height: 36px !important;
  font-size: 13px !important;
  letter-spacing: 0.3px;
}
.booking-checkin-btn:hover,
.booking-checkin-btn:focus {
  background: #15803d !important;
  border-color: #15803d !important;
}

/* Split badge on the table card */
.split-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: linear-gradient(135deg, #2563eb, #60a5fa);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  border-radius: 12px;
  padding: 3px 8px;
  z-index: 5;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
  letter-spacing: 0.3px;
}
.split-badge:hover {
  background: linear-gradient(135deg, #1d4ed8, #3b82f6);
}
.split-badge .fa {
  margin-right: 4px;
}

/* Split-invoices modal */
.split-invoices-modal-title {
  color: #2563eb;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
}
.split-invoices-modal-sub {
  color: #9ca3af;
  font-weight: 500;
  font-size: 13px;
}

/* Summary strip */
.split-invoices-summary {
  display: flex;
  align-items: center;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 14px;
}
.summary-block {
  flex: 1;
  text-align: center;
}
.summary-label {
  font-size: 10px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}
.summary-value {
  font-size: 16px;
  font-weight: 700;
  color: #111;
  margin-top: 2px;
}
.summary-total {
  color: #16a34a;
  font-size: 18px;
}
.summary-divider {
  width: 1px;
  align-self: stretch;
  background: #e5e7eb;
  margin: 0 6px;
}

/* Bill card */
.split-invoice-card {
  display: flex;
  align-items: stretch;
  gap: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px 14px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
}
.split-invoice-card:hover {
  border-color: #2563eb;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.08);
  transform: translateY(-1px);
}
.split-invoice-card:hover .split-invoice-action {
  background: #16a34a;
  color: #fff;
}
.split-invoice-mt {
  margin-top: 8px;
}
.split-invoice-left {
  flex: 1;
  min-width: 0;
}
.split-invoice-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
}
.split-invoice-tag-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}
.split-invoice-tag {
  background: #2563eb;
  color: #fff;
  border-radius: 10px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
}
.split-invoice-no {
  font-size: 11px;
  color: #9ca3af;
  font-weight: 600;
}
.split-invoice-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 6px;
}
.split-invoice-meta .fa {
  color: #60a5fa;
  margin-right: 4px;
}
.split-invoice-items {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 6px;
  font-size: 11px;
  color: #4b5563;
}
.split-invoice-item {
  background: #f3f4f6;
  border-radius: 4px;
  padding: 1px 7px;
}
.split-invoice-item .qty {
  color: #2563eb;
  font-weight: 700;
  margin-left: 2px;
}
.split-invoice-item.more {
  background: transparent;
  color: #9ca3af;
  font-style: italic;
  padding: 1px 4px;
}
.split-invoice-amount {
  font-size: 20px;
  font-weight: 800;
  color: #16a34a;
  line-height: 1;
  white-space: nowrap;
}
.split-invoice-action {
  font-size: 11px;
  font-weight: 700;
  color: #16a34a;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  padding: 4px 10px;
  letter-spacing: 0.3px;
  transition: all 0.15s;
  white-space: nowrap;
}
.split-invoice-action .fa { margin-right: 4px; }

.split-invoices-empty {
  text-align: center;
  padding: 28px 16px;
  color: #16a34a;
}
.split-invoices-empty .fa {
  font-size: 28px;
  display: block;
  margin-bottom: 8px;
}
.split-invoices-empty p {
  color: #6b7280;
  margin: 0;
  font-size: 13px;
}
</style>